import { type InputCapture } from '@canvas/input';
import { type Capture } from '@canvas/input/types';
import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

export type EventSubscriber = (events: Capture[]) => void;
export type SyncKeyboardDownSubscriber = (event: KeyboardDownCapture) => void;

export interface SharedEventLoop {
  subscribe(fn: EventSubscriber): () => void;
  /**
   * Subscribes to keydown captures before the RAF-batched event loop.
   *
   * Use this only when the browser needs a synchronous response before its
   * native default action runs. Most keyboard inputs should use `subscribe`.
   */
  subscribeSyncKeyboardDown(fn: SyncKeyboardDownSubscriber): () => void;
  start(): void;
  stop(): void;
}

/**
 * Creates a single shared event loop that allows us to share among keyboard and mouse.
 */
export function createSharedEventLoop(inputCapture: InputCapture): SharedEventLoop {
  const subscribers = new Set<EventSubscriber>();
  const syncKeyboardDownSubscribers = new Set<SyncKeyboardDownSubscriber>();
  let frameId: number | null = null;

  inputCapture.on('keyboardDown', (event: KeyboardDownCapture) => {
    syncKeyboardDownSubscribers.forEach((fn) => fn(event));
  });

  function processFrame() {
    const events = inputCapture.getEvents();

    // Broadcast all events to all subscribers (Mouse, Keyboard, etc.)
    if (events.length > 0) {
      subscribers.forEach((fn) => fn(events));
    }

    // Safely clear the queue exactly once per frame
    inputCapture.clear();

    frameId = requestAnimationFrame(processFrame);
  }

  return {
    subscribe(fn: EventSubscriber) {
      subscribers.add(fn);

      return () => subscribers.delete(fn);
    },

    subscribeSyncKeyboardDown(fn: SyncKeyboardDownSubscriber) {
      syncKeyboardDownSubscribers.add(fn);

      return () => syncKeyboardDownSubscribers.delete(fn);
    },

    start() {
      if (frameId !== null) {
        return;
      }

      frameId = requestAnimationFrame(processFrame);
    },

    stop() {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);

        frameId = null;
      }
    },
  };
}
